import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import leiOrcamentariaService, { TIPO_LEI_LABEL, type LeiOrcamentaria, type TipoLei } from '../services/leiOrcamentariaService';
import { UI_THEME } from '../ui/theme';
import { FIELD_CLASS, FIELD_BASE_CLASS } from '../components/ui/fieldStyles';
import { toUpperText } from '../utils/formatters';

const anoAtual = new Date().getFullYear();

const formVazio = () => ({ tipo: 'LOA' as TipoLei, exercicio: String(anoAtual), numero: '', descricao: '', url: '' });

const mensagemErro = (err: unknown, padrao: string) =>
  (axios.isAxiosError<{ error?: string }>(err) && err.response?.data?.error) || padrao;

/**
 * Cadastro de leis orçamentárias por exercício (TAEP B.2). O Orçamento escolhe a lei
 * na tela da emenda; corrigir o número ou o link aqui atualiza as emendas vinculadas.
 */
const LeisOrcamentariasPage: React.FC = () => {
  const isSaas = UI_THEME === 'saas';
  const [leis, setLeis] = useState<LeiOrcamentaria[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtroExercicio, setFiltroExercicio] = useState('');
  const [form, setForm] = useState(formVazio());
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setLeis(await leiOrcamentariaService.listar());
      setErro(null);
    } catch (err) {
      setErro(mensagemErro(err, 'Não foi possível carregar as leis.'));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  const editando = useMemo(() => leis.find((l) => l.id === editandoId) ?? null, [leis, editandoId]);
  const bloqueiaIdentidade = !!editando && editando.emendasVinculadas > 0;

  const porExercicio = useMemo(() => {
    const grupos = new Map<number, LeiOrcamentaria[]>();
    leis
      .filter((l) => !filtroExercicio || String(l.exercicio) === filtroExercicio)
      .forEach((l) => grupos.set(l.exercicio, [...(grupos.get(l.exercicio) ?? []), l]));
    return [...grupos.entries()].sort((a, b) => b[0] - a[0]);
  }, [leis, filtroExercicio]);

  const exercicios = useMemo(() => [...new Set(leis.map((l) => l.exercicio))].sort((a, b) => b - a), [leis]);

  const iniciarEdicao = (lei: LeiOrcamentaria) => {
    setEditandoId(lei.id);
    setErro(null);
    setForm({ tipo: lei.tipo, exercicio: String(lei.exercicio), numero: lei.numero, descricao: lei.descricao ?? '', url: lei.url });
  };

  const cancelar = () => { setEditandoId(null); setForm(formVazio()); setErro(null); };

  const salvar = async (event: React.FormEvent) => {
    event.preventDefault();
    setErro(null);
    setSalvando(true);
    const payload = {
      tipo: form.tipo,
      exercicio: Number(form.exercicio),
      numero: form.numero.trim(),
      descricao: form.descricao.trim() || undefined,
      url: form.url.trim(),
    };
    try {
      if (editandoId) await leiOrcamentariaService.atualizar(editandoId, payload);
      else await leiOrcamentariaService.criar(payload);
      cancelar();
      await carregar();
    } catch (err) {
      setErro(mensagemErro(err, 'Não foi possível salvar a lei.'));
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (lei: LeiOrcamentaria) => {
    try {
      await leiOrcamentariaService.definirAtivo(lei.id, !lei.ativo);
      await carregar();
    } catch (err) {
      setErro(mensagemErro(err, 'Não foi possível alterar a situação da lei.'));
    }
  };

  const superficie = 'rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900';
  const input = FIELD_CLASS;
  const rotulo = 'text-sm font-medium text-slate-700 dark:text-slate-200';
  const botaoPrimario = `rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 bg-primary hover:bg-primary-hover`;
  const botaoSecundario = 'rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  return (
    <div className="w-full space-y-6">
      <section className={superficie}>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-sky-700 dark:text-sky-400'}`}>Orçamento</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-100">Leis orçamentárias</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
          Cadastre a LOA de cada exercício com o link da lei. Na tela da emenda, o Orçamento escolhe a lei e o portal da
          transparência passa a publicar número, ano e link.
        </p>
      </section>

      <section className={superficie} aria-labelledby="titulo-form-lei">
        <h2 id="titulo-form-lei" className="text-lg font-bold text-slate-950 dark:text-slate-100">
          {editandoId ? 'Editar lei' : 'Nova lei'}
        </h2>
        <form onSubmit={salvar} className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <label className={rotulo}>
            Tipo
            <select value={form.tipo} disabled={bloqueiaIdentidade} onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value as TipoLei }))} className={input}>
              <option value="LOA">{TIPO_LEI_LABEL.LOA}</option>
              <option value="CREDITO_ADICIONAL">{TIPO_LEI_LABEL.CREDITO_ADICIONAL}</option>
            </select>
          </label>
          <label className={rotulo}>
            Exercício
            <input type="number" min={2000} max={anoAtual + 5} required value={form.exercicio} disabled={bloqueiaIdentidade}
              onChange={(e) => setForm((f) => ({ ...f, exercicio: e.target.value }))} className={input} />
          </label>
          <label className={rotulo}>
            Número da lei
            <input type="text" maxLength={50} required value={form.numero} placeholder="Ex.: Lei nº 3.456/2025"
              onChange={(e) => setForm((f) => ({ ...f, numero: toUpperText(e.target.value) }))} className={input} />
          </label>
          <label className={rotulo}>
            Descrição (opcional)
            <input type="text" maxLength={300} value={form.descricao}
              onChange={(e) => setForm((f) => ({ ...f, descricao: toUpperText(e.target.value) }))} className={input} />
          </label>
          <label className={`${rotulo} md:col-span-2 lg:col-span-4`}>
            Link público da lei
            <input type="url" maxLength={1000} required value={form.url} placeholder="https://..."
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} className={input} />
          </label>
          {bloqueiaIdentidade && (
            <p className="text-sm text-amber-800 dark:text-amber-300 md:col-span-2 lg:col-span-4">
              Esta lei está vinculada a {editando?.emendasVinculadas} emenda(s): tipo e exercício não podem mudar. Número, descrição e link
              podem ser corrigidos e a correção vale para as emendas vinculadas.
            </p>
          )}
          {erro && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 md:col-span-2 lg:col-span-4">{erro}</p>}
          <div className="flex gap-3 md:col-span-2 lg:col-span-4">
            <button type="submit" disabled={salvando} className={botaoPrimario}>{salvando ? 'Salvando...' : editandoId ? 'Salvar alterações' : 'Cadastrar lei'}</button>
            {editandoId && <button type="button" onClick={cancelar} className={botaoSecundario}>Cancelar</button>}
          </div>
        </form>
      </section>

      <section className={superficie} aria-labelledby="titulo-lista-leis">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="titulo-lista-leis" className="text-lg font-bold text-slate-950 dark:text-slate-100">Leis cadastradas</h2>
          <label className={`${rotulo} flex items-center gap-2`}>
            Exercício
            <select value={filtroExercicio} onChange={(e) => setFiltroExercicio(e.target.value)} className={FIELD_BASE_CLASS}>
              <option value="">Todos</option>
              {exercicios.map((ano) => <option key={ano} value={ano}>{ano}</option>)}
            </select>
          </label>
        </div>

        {carregando ? (
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">Carregando...</p>
        ) : porExercicio.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-600 dark:border-slate-600 dark:text-slate-300">
            Nenhuma lei cadastrada{filtroExercicio ? ` para ${filtroExercicio}` : ''}.
          </p>
        ) : (
          <div className="mt-4 space-y-6">
            {porExercicio.map(([ano, lista]) => (
              <div key={ano}>
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Exercício {ano}</h3>
                <ul className="mt-2 divide-y divide-slate-200 rounded-xl border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
                  {lista.map((lei) => (
                    <li key={lei.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 dark:text-slate-100">
                          {lei.numero}
                          <span className="ml-2 text-xs font-medium text-slate-500 dark:text-slate-400">{TIPO_LEI_LABEL[lei.tipo] ?? lei.tipo}</span>
                          {!lei.ativo && <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">Desativada</span>}
                        </p>
                        {lei.descricao && <p className="text-sm text-slate-600 dark:text-slate-300">{lei.descricao}</p>}
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          <a href={lei.url} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-700 hover:underline dark:text-blue-400">Abrir lei →</a>
                          {' · '}{lei.emendasVinculadas} emenda(s) vinculada(s)
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => iniciarEdicao(lei)} className={botaoSecundario}>Editar</button>
                        <button type="button" onClick={() => void alternarAtivo(lei)} className={botaoSecundario}>{lei.ativo ? 'Desativar' : 'Ativar'}</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default LeisOrcamentariasPage;
